'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import AccordionSection from '@/app/[locale]/(default-layout)/(forms)/_components/AccordionSection';
import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';
import KidsNTeensForm from '@/app/[locale]/(default-layout)/(forms)/_components/KidsNTeensForm';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

type KidsNTeensPageClientProps = {
    domainConfiguration: DomainConfigurationDto;
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const KidsNTeensPageClient: React.FC<KidsNTeensPageClientProps> = ({ domainConfiguration, hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'kidsNTeens');

    const heroImage = resolveFormImageUrl(domainConfiguration, 'kidsNTeens', hallImageUrl);
    const customDescription = resolveFormPageText(domainConfiguration, 'kidsNTeens');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'kidsNTeens');

    return (
        <main className="min-h-screen">
            {/* Hero Section */}
            <div className="py-[96px] space-y-[48px]">
                <section>
                    <CenterLayout>
                        <div className="text-center space-y-8">
                            <div className="space-y-4">
                                <h1
                                    className="text-headline-32"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {customPageTitle || t('kids-n-teens.hero.title')}
                                </h1>
                                {!customDescription && (
                                    <h2
                                        className="text-headline-24 font-semibold"
                                        style={{ color: domainConfiguration.style.primaryColor }}>
                                        {t('kids-n-teens.hero.subtitle')}
                                    </h2>
                                )}
                            </div>
                            <div
                                className="text-body-18 max-w-3xl mx-auto"
                                style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                                {customDescription ? (
                                    <CinemaDescriptionMarkdown
                                        description={customDescription}
                                        allowInlineHtml
                                    />
                                ) : (
                                    t('kids-n-teens.hero.description')
                                )}
                            </div>
                            {heroImage && isMounted && (
                                <div className="relative w-full max-w-3xl mx-auto aspect-[16/9] rounded-12 overflow-hidden">
                                    <Image
                                        src={heroImage}
                                        alt={t('kids-n-teens.hero.title')}
                                        fill
                                        className="object-cover"
                                        sizes="(max-width: 768px) 100vw, 768px"
                                    />
                                </div>
                            )}
                        </div>
                    </CenterLayout>
                </section>

                {/* Benefits section is default copy, so a custom page text replaces it too —
                    otherwise the operator's wording would sit above boilerplate they cannot edit. */}
                {!customDescription && (
                    <section>
                        <CenterLayout>
                            <div className="max-w-6xl mx-auto">
                                <h2
                                    className="text-headline-32 text-center mb-16"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {t('kids-n-teens.benefits.title')}
                                </h2>
                                <div className="grid md:grid-cols-2 gap-12">
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                €
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.free-membership')}
                                        </h3>
                                    </div>
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                €
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.reduced-prices')}
                                        </h3>
                                    </div>
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                🍿
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.special-menu')}
                                        </h3>
                                    </div>
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                ⭐
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.points-system')}
                                        </h3>
                                    </div>
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                📧
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.newsletter')}
                                        </h3>
                                    </div>
                                    <div
                                        className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300"
                                        style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                                        <div
                                            className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-[32px] font-bold drop-shadow-sm"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                🎫
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-20"
                                            style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                            {t('kids-n-teens.benefits.welcome-ticket')}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>
                )}
            </div>

            {/* Extras and age groups are default copy as well, so a custom page text replaces the
                whole editorial part of the page — only the form and the contact block remain. */}
            {!customDescription && (
                <>
                    {/* Extras & Vorteile Section */}
                    <section
                        className="py-32"
                        style={{ backgroundColor: withOpacity(domainConfiguration.style.onBackgroundColor, 0.05) }}>
                        <CenterLayout>
                            <div className="max-w-4xl mx-auto">
                                <h2
                                    className="text-headline-32 text-center mb-16"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {t('kids-n-teens.extras.title')}
                                </h2>
                                <div className="space-y-6">
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.membership.title')}
                                        domainConfiguration={domainConfiguration}
                                        defaultOpen={true}>
                                        {t('kids-n-teens.extras.membership.description')}
                                    </AccordionSection>
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.everyday-cinema.title')}
                                        domainConfiguration={domainConfiguration}>
                                        {t('kids-n-teens.extras.everyday-cinema.description')}
                                    </AccordionSection>
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.junior-menu.title')}
                                        domainConfiguration={domainConfiguration}>
                                        {t('kids-n-teens.extras.junior-menu.description')}
                                    </AccordionSection>
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.points-rewards.title')}
                                        domainConfiguration={domainConfiguration}>
                                        {t('kids-n-teens.extras.points-rewards.description')}
                                    </AccordionSection>
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.exclusive-info.title')}
                                        domainConfiguration={domainConfiguration}>
                                        {t('kids-n-teens.extras.exclusive-info.description')}
                                    </AccordionSection>
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.welcome-gift.title')}
                                        domainConfiguration={domainConfiguration}>
                                        {t('kids-n-teens.extras.welcome-gift.description')}
                                    </AccordionSection>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>

                    {/* Age Groups Section */}
                    <section className="py-32">
                        <CenterLayout>
                            <div className="max-w-6xl mx-auto">
                                <h2
                                    className="text-headline-32 text-center mb-16"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {t('kids-n-teens.age-groups.title')}
                                </h2>
                                <div className="grid md:grid-cols-2 gap-12">
                                    <div className="text-center space-y-6">
                                        <div
                                            className="size-24 mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-36 font-bold"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                👶
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-24"
                                            style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                            {t('kids-n-teens.age-groups.kids.title')}
                                        </h3>
                                        <p
                                            className="text-body-16"
                                            style={{
                                                color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8)
                                            }}>
                                            {t('kids-n-teens.age-groups.kids.description')}
                                        </p>
                                    </div>
                                    <div className="text-center space-y-6">
                                        <div
                                            className="size-24 mx-auto rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                                            <span
                                                className="text-36 font-bold"
                                                style={{ color: domainConfiguration.style.onPrimaryColor }}>
                                                🧒
                                            </span>
                                        </div>
                                        <h3
                                            className="text-headline-24"
                                            style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                            {t('kids-n-teens.age-groups.teens.title')}
                                        </h3>
                                        <p
                                            className="text-body-16"
                                            style={{
                                                color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8)
                                            }}>
                                            {t('kids-n-teens.age-groups.teens.description')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>
                </>
            )}

            {/* Registration Form Section */}
            <section className="py-32">
                <CenterLayout>
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-12">
                            <h2
                                className="text-headline-32 mb-4"
                                style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                {t('kids-n-teens.form.title')}
                            </h2>
                            <p
                                className="text-body-16"
                                style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                                {t('kids-n-teens.form.subtitle')}
                            </p>
                        </div>

                        <KidsNTeensForm domainConfiguration={domainConfiguration} />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section
                className="py-32 backdrop-blur-sm"
                style={{ backgroundColor: withOpacity(domainConfiguration.style.onBackgroundColor, 0.05) }}>
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2
                            className="text-headline-32"
                            style={{ color: domainConfiguration.style.onBackgroundColor }}>
                            {t('kids-n-teens.contact.question')}
                        </h2>
                        <p
                            className="text-body-16 max-w-2xl mx-auto"
                            style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                            {t('kids-n-teens.contact.help-text')}
                        </p>
                        <FormContactLinks
                            domainConfiguration={domainConfiguration}
                            namespace="kids-n-teens"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default KidsNTeensPageClient;
